import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IconCode } from "@multiweb/types/contracts";
import { ExtendedPropertiesType } from "@multiweb/types/multiweb";
import { ValidationErrorType } from "OW.Remoting/Basic/EOWExceptionsInterfaces";
import { getExtendedPropertiesFromElement } from "UI/utils";
import { Event } from "../../../../../Global/Base/Events";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { EOWUserException, EOWValidateException } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { IInteractive } from "../../../../CommonUIInterfaces";
import { IBussyAwaitable, IEnabledVisualControl, IInteractiveControl } from "../../../../UI.BaseControls/ControlInterfaces";
import { ActionContext, ActionType, IAction, IActionContext, SelectionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { convertContextFromFrameToRequest } from "../../../Utilities/SharedUIMethods";
import { Control } from "../../Controls/Control";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { UIManager } from "../../UIManager";
import { IValidationMaster } from "../FormControl";
import { IMasterableFrame } from "../Frames/FrameInterfaces";
import { IForm, IFormSavingParameters } from "../IForm";
import { IFrame } from "../IFrame";
import { ObjectCheckingResult, SharedMethods } from "../SharedMethods";
import { IValidationOverview } from "../ValidationManager";
import { IPrimary } from "./IPrimary";
import { DesktopToolbarButton } from "./ToolbarButton";
import { IUseMasterObject } from "Global/Base/Rights/IUseMasterObject";
import { IActionExecutionContext, ActionLocalExecutionContext, ActionMasterExecutionContext } from "./ActionExecutionContext";

const logger = LazyLoadingSectionLogger.create(LoggingSection.xgAction);

export abstract class XGAction extends Control implements IInteractiveControl, IAction, IPrimary, IInteractive, IEnabledVisualControl, IUseMasterObject {
    //#region Fields
    /**true - je zobrazena v nodu groupy, false - je zobrazena primo v grupe*/
    public isInNode: boolean = false;
    public action: () => Promise<void> = async () => {
        // Prázdná metoda
    };
    /**
     * Při nastavení na true nebude provádět autosave před spuštěním akce bez ohledu na nastavení formuláře
     */
    public ignoreAutoSave: boolean;
    private _ownerForm: IForm;

    /**
     * V tomto elementu je html definice pro action html reprezentace pro vykresleni je definovana v ToolBarButtonu nebo klasicke komponente element
     */
    public definitionElement: HTMLElement;
    public toMenu?: MenuType;
    /**
     * Viditelnost akce podle viditelnosti tlačítka, které tuto akci vytvořilo. Pokud akce nebyla vytvořena tlačítkem, tato vlastnost je vždy True
     */
    public visibleByButton: boolean;
    /**
     * Přístupnost akce podle přístupnosti tlačítka, které tuto akci vytvořilo. Pokud akce nebyla vytvořena tlačítkem, tato vlastnost je vždy True
     */
    public isEnabledByButton: boolean = true;
    /**
     * Jmeno nadrazeneho prvku toolbaru, do ktereho se akce zaradi, skupina, stranka nebo nodeaction
     */
    public masterNode: string;
    /**
     * Pozice akce v toolbaru v ramci skupiny
     */
    public priority: number;
    /**
     * Pozice akce dle pořadí v definici formuláře
     */
    public order: number = null; // Musí mít hodnotu, jinak se nevyhodnocuje instanceof
    /**
     * Speciální ikona pro akci zadaná v definici formuláře (má přednost nad standardní ikonou)
     */
    public icon?: IconCode;
    /**
     * Defaultni pro poradi akce ve skupine
     */
    public static readonly defaultPriority: number = 9999;
    private _owner: IFrame;
    private readonly _useMasterObject: boolean = false;
    protected override _visible: boolean = false;
    protected override _enabled: boolean = false;
    protected _isActionDisposed: boolean = false;
    protected _useMasterContext: boolean = false;
    public readonly executionContext: IActionExecutionContext;

    /**
     * Akce je přístupná pouze v edit. módu
     */
    public readonly enableInEdit: boolean = false;

    public readonly enabledChanged: Event<XGAction, boolean> = new Event<XGAction, boolean>();
    public override readonly visibilityChanged: Event<XGAction, boolean> = new Event<XGAction, boolean>();
    public readonly interactiveChanged: Event<XGAction, boolean> = new Event<XGAction, boolean>();
    public readonly enabledVisualChanged: Event<XGAction, boolean> = new Event<XGAction, boolean>();
    public readonly isActiveChanged: Event<XGAction, boolean> = new Event<XGAction, boolean>();
    public readonly ownerChanged: Event<XGAction, IFrame> = new Event<XGAction, IFrame>();

    /**
     * Interní příznak, zda byla akce aktivní (kombinace Enabled a isReadOnly).
     * Používá se k porovnání předchozího stavu.
     */
    protected _oldIsActive = true;
    protected _interactive: boolean = true;
    protected _enabledVisual: boolean = true;

    public readonly isPrimary: boolean = false;

    private _toolbarButton: DesktopToolbarButton;
    private _actionSize: string;
    private _hasCaption: boolean = true; //Určuje jestli bude příslušející ToolbarButton mít viditelný popisek
    //#endregion Fields

    //#region Properties
    public get id(): string | undefined {
        return this.element ? this.element.id : undefined;
    }

    /** @inheritdoc */
    public get useMasterObject(): boolean {
        return this._useMasterObject;
    }

    public get toolbarButton(): DesktopToolbarButton {
        return this._toolbarButton;
    }

    public set toolbarButton(button: DesktopToolbarButton) {
        this._toolbarButton = button;
        if (!this._toolbarButton) {
            return;
        }

        // po změně tlačítka je potřeba mu správně nastavit aktuální stav Enabled
        this.enabledChanged.trigger(this, this.enabled); // TODO-rites
        if (!this.hasCaption)
            this._toolbarButton.element.classList.add("no-caption");
    }

    public get ownerForm(): IForm {
        if (this._ownerForm)
            return this._ownerForm;
        else
            return this.owner.ownerForm;
    }

    public set ownerForm(button: IForm) {
        this._ownerForm = button;
    }

    public override set visible(value: boolean) {
        if (this.element == undefined)
            return;

        if (this._visible !== value) {
            this._visible = value;
            this.visibilityChanged.trigger(this, value);
        }
    }

    public override get visible(): boolean {
        return this._visible;
    }

    // Nastavuje přístupnost akce (Enable/Disable)
    public override get enabled(): boolean {
        return this._enabled;
    }

    public override set enabled(value: boolean) {
        if (this._enabled !== value) {
            this._enabled = value;
            this.enabledChanged.trigger(this, value); // TODO-rites
            //this._updateActiveState();  // TODO-rites
        }
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;

        this.interactiveChanged.trigger(this, value);
    }

    public get enabledVisual(): boolean {
        return this._enabledVisual;
    }

    public set enabledVisual(value: boolean) {
        const oldValue = this._enabledVisual;
        this._enabledVisual = value;

        if (oldValue !== value) {
            this.enabledVisualChanged.trigger(this, value);
        }
        this._updateActiveState(); // TODO-rites
    }

    /**
     * Příznak, zda je akce aktivní. Kombinuje Enabled a interactive
     */
    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public get actionType(): ActionType {
        throw new Error("Undefined actionType");
    }

    public get supportedSelectionModes(): SelectionType {
        return SelectionType.single;
    }

    public get context(): ActionContext {
        throw new Error("Undefined context");
    }

    public override get locKey(): string {
        if (super.locKey) {
            return super.locKey;
        } else {
            this.locKey = this.element.getAttribute("data-lockey");
            return super.locKey;
        }
    }

    public override set locKey(value: string) {
        super.locKey = value;
    }

    public get actionSize(): string {
        if (this._actionSize == null)
            this._actionSize = "asDefault";

        return this._actionSize;
    }

    public set actionSize(value: string) {
        if (value == null)
            this._actionSize = "asDefault";
        else
            this._actionSize = value;
    }

    public set owner(v: IFrame) {
        this._owner = v;
        this.ownerChanged.trigger(this, this._owner);
    }

    public get owner(): IFrame {
        return this._owner;
    }

    public get hasCaption(): boolean {
        return this._hasCaption;
    }

    public set hasCaption(value: boolean) {
        this._hasCaption = value;
        if (this._toolbarButton)
            this._toolbarButton.element.classList.toggle("no-caption", !value);
    }

    private readonly _saveOnExecute: boolean;
    /** Příznak, jestli metoda uloží objekt před provedením */
    public get saveOnExecute(): boolean {
        return this._saveOnExecute;
    }

    public get extendedProperties(): ExtendedPropertiesType {
        return getExtendedPropertiesFromElement(this.element);
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: ControlParams) {
        super(params);

        this.element.classList.add("XGAction");

        this.masterNode = this.element.getAttribute("data-masternode");
        this.isPrimary = this.getBoolDataAttr("isPrimary");

        this.actionSize = this.getDataAttr("ActionSize");
        const iconName = this.getDataAttr("IconName")?.trim();
        if (EnumUtils.hasEnumValue(IconCode, iconName))
            this.icon = iconName as IconCode;
        else
            this.icon = null;

        this.enabledVisual = false;

        this.priority = this.getNumDataAttr("priority");
        if (!this.priority)
            this.priority = XGAction.defaultPriority;
        this._saveOnExecute = this.getBoolDataAttr("saveonexecute", false);
        this._useMasterContext = this.getBoolDataAttr("UseMasterContext", false);
        this.enableInEdit = this.getBoolDataAttr("enableInEdit", false);
        this._useMasterObject = this.getBoolDataAttr("UseMasterObject", false);
        this.executionContext = this._useMasterObject ?
            new ActionMasterExecutionContext(this) :
            new ActionLocalExecutionContext(this);
    }
    //#endregion Constructors

    //#region Methods
    public setToMenu(defaultByAction: MenuType): void {
        const toMenu = this.element.getAttribute("data-tomenu");
        const toMenuEnumValue = EnumUtils.getEnumValueFromName(MenuType, toMenu);

        this.toMenu = toMenuEnumValue != null ? toMenuEnumValue : defaultByAction;
    }

    public inicializeRights(_frame: IFrame): void {
        // Prázdná metoda
    }

    public clearDataLinks(): void {
        // Prázdná metoda
    }

    /**
     * Podle formName formuláře získá právo na Insert
     * <param name="formName">Název formuláře</param>
     * <returns>Hodnota práva na Insert</returns>
     */
    public getInsertRightFromForm(_formName: string): void {
        // Prázdná metoda
    }

    /**
     * Podle formName formuláře získá právo na Edit
     * <param name="formName">Název formuláře</param>
     * <returns>Hodnota práva na Edit</returns>
     */
    public getEditRightFromForm(_formName: string): boolean {
        return false;
    }

    /**
     * Nastavuje viditelnost akce
     * <param name="visibility"></param>
     */
    public setVisibility(visibility: boolean): void {
        //pokud akce byla vytvořena s tlačítka, nemá vlastní kolekci práv. A proto pokud viditelnost akce se vyhodnotila později (vždy bude True), než viditelnost tlačítka,
        //musíme spočítat s hodnotou tohoto práva na tlačítku. Jinak tlačítko nastaví viditelnost akce podle svého vyhodnoceného práva ve své metodě SetVisibility
        const newValue = visibility && this.visibleByButton;
        if (this.visible !== newValue) {
            this.visible = visibility && this.visibleByButton;
            this.visibilityChanged.trigger(this, newValue);
        }
    }

    /**
     * Aktualizuje stav aktivnosti elementu podle isActive. A pokud byl změněn vyvolá událost isActiveChange.
     */
    protected _updateActiveState(): void {
        if (this.checkAndLogDisposedUsage("_updateActiveState")) return;

        this.element.classList.toggle("Enabled", this.enabledVisual);
        if (this.isActive !== this._oldIsActive) {
            this._oldIsActive = this.isActive;
            this.isActiveChanged.trigger(this, this.isActive);
        }
    }
    //#endregion Methods

    public getImageName(): string {
        return "";
    }

    public async execute(context?: IActionContext): Promise<void> {
        const owner = this.executionContext.ownerFrame ?? this.owner;

        if (owner instanceof IBussyAwaitable)
            await owner.waitForPendingActions();

        UIManager.instance.showLoader();
        try {
            if (this.executionContext.ownerFrame)
                await this.executionContext.ownerFrame.ownerForm.waitForBussyFrameAndChildren(owner);

            await this._internalExecute(context);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     *
     * @param id V případě, že je akce spouštěna přes ID objektu místo instance
     * @param className V případě, že je akce spouštěna přes ID objektu místo instance
     */
    protected abstract _internalExecute(context?: IActionContext): Promise<void>;

    /**
     * validace práva akcí všech potřebných komponent na owner framu.
     * @param checkRecommended zda se mají kontrolovat doporučená pole
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @return true pokud validace proběhla v pořádku, jinak false
     * @protected
     */
    protected async _validateOwnerRightsActions(checkRecommended: boolean, checkPostponed: boolean): Promise<boolean> {
        if (!checkPostponed && !checkRecommended) {
            return false;
        }

        if (!this.executionContext.ownerFrame) {
            return true;
        }

        if (this.executionContext.ownerFrame instanceof IValidationMaster) {
            return await this.executionContext.ownerFrame.validate(checkRecommended, checkPostponed, true);
        }
        return false;
    }

    /**
     * Zvaliduje atribut postponed u aktuálního objektu a spolu s výsledkem validace práv akcí na owner formu (parametrem `validationResult`) zkontroluje jejich obsah.
     * Pokud se při validaci objevily nějaké chyby, zprocesuje je a zobrazí dialog pro další interakci uživatele.
     * @param checkRecommended zda se mají kontrolovat doporučená pole
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @param validationResult výsledek validace práv akcí na owner formu
     * @param runButtonText lokalizace pro tlačítko yes v yes/no dialogu
     * @return true pokud kontrola práv akcí proběhla bez chyb, jinak false
     * @protected
     */
    protected async _checkPostRightsActions(checkRecommended: boolean, checkPostponed: boolean, validationResult?: IValidationOverview, runButtonText?: string): Promise<boolean> {
        if (!checkPostponed && !checkRecommended) {
            return true;
        }

        if (this.executionContext.ownerFrame instanceof IValidationMaster) {
            await this._validatePostponed(checkPostponed, validationResult?.hasErrors);

            const validationMessages = validationResult ? validationResult.validationMessages : [];

            const res = await SharedMethods.checkMessagesBeforeExecute(validationMessages, runButtonText ? runButtonText : this.locKey, "GUI-33412"); //"GUI-33412"=Zrušit
            if (res === ObjectCheckingResult.error || res === ObjectCheckingResult.recommendedStop || !validationResult?.ownerValidated) {
                return false;
            }
        }
        return true;
    }

    /**
     * Zkontroluje validitu postponed atributu aktuálního objektu.
     * Pokud se při kontrole atributu objeví chyby (vyhodí se výjimka typu `EOWValidateException` nebo `EOWUserException`), zpracuje ji a případně zobrazí dialog pro další interakci.
     * Pokud je při kontrole vyhozena výjimka jiného typu, přepošle ji volající metodě.
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @param hasErrors zda na ownerovi existuje nějaká validační chyba
     * @private
     */
    private async _validatePostponed(checkPostponed: boolean, hasErrors: boolean): Promise<void> {
        const finalResultMsg: SaveResultData[] = [];
        const { ownerFrame } = this.executionContext;
        if (ownerFrame instanceof IValidationMaster && checkPostponed && !hasErrors) {
            const { actualObject } = this.executionContext;
            try {
                // PostponedValidate je obdoba validate, akorát se nic nekontroluje v aplikační logice, ale jen práva na službě (některé atributy jsou postReq, ale na formuláři nejsou vykreslené).
                await actualObject?.postponedValidate();
            } catch (e) {
                if (e instanceof EOWValidateException) {
                    e.validationErrors.forEach(validationError => {
                        validationError.type = ValidationErrorType.postponedError;
                    });
                    finalResultMsg.push({ data: actualObject, errors: e.validationErrors });
                } else if (e instanceof EOWUserException) {
                    finalResultMsg.push({ data: actualObject, messages: e.resultMessages });
                } else {
                    throw e;
                }
            } finally {
                if (ownerFrame instanceof IMasterableFrame) {
                    await SharedMethods.processSaveResultsAndShowMessages(ownerFrame, finalResultMsg);
                }
            }
        }
    }

    /**
     * Zvaliduje práva akce a zprocesuje její výsledky. Pokud validace odhalila nevalidní komponenty, zkontroluje je a případně zobrazí dialog pro další interakci s uživatelem.
     * @param checkRecommended zda se mají kontrolovat doporučená pole
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @param runButtonText lokalizace pro tlačítko yes v yes/no dialogu
     * @return true pokud kontrola práv akcí proběhla bez chyb, jinak false
     * @private
     */
    protected async _checkRights(checkRecommended: boolean, checkPostponed: boolean, runButtonText?: string): Promise<boolean> {
        if (!checkPostponed && !checkRecommended) {
            return true;
        }

        const { ownerFrame } = this.executionContext;
        if (ownerFrame instanceof IValidationMaster) {
            const ownerValidated = await ownerFrame.validate(checkRecommended, checkPostponed, true);

            await this._validatePostponed(checkPostponed, ownerFrame.validationManager.hasErrors);

            const res = await SharedMethods.checkObjectBeforeExecute(ownerFrame.validationManager, runButtonText ? runButtonText : this.locKey, "GUI-33412"); //"GUI-33412"=Zrušit
            if (res === ObjectCheckingResult.error || res === ObjectCheckingResult.recommendedStop || !ownerValidated) {
                return false;
            }
        }
        return true;
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest((this.executionContext.ownerFrame || this.owner).frameContext, cancellationToken);
    }

    protected async _checkAndSaveForm(params?: IFormSavingParameters): Promise<boolean> {
        if (this.enableInEdit) {
            return true;
        }
        if (this._useMasterContext) {
            return await this.ownerForm.checkAndSaveForm({ forceSave: true, skipSavingEditContextId: true, ...params });
        }
        return await this.ownerForm.checkAndSaveForm({ forceSave: this.saveOnExecute, ...params });
    }

    /**
     * Zkontroluje, zda je akce disposed a pokud ano zaloguje warning o volání metody nad disposed akcí.
     *
     * Tato metoda a obecně kontroly na dispose by neměly být nutné po odstranění příčin, kvůli kterým se pracuje i nadále s disposed objekty.
     * Pro řešení příčin je vytvořený task 33553.
     *
     * @param methodName Název metody, kde se provádí kontrola. Slouží pro logování.
     * @return true, pokud je akce disposed, jinak false.
     */
    protected checkAndLogDisposedUsage(methodName: string): boolean {
        const disposed = this.isDisposed();
        if (disposed) {
            logger.warn(`Calling ${methodName} on disposed ${this.constructor.name}!`);
        }
        return disposed;
    }

    public isDisposed(): boolean {
        return this._isActionDisposed;
    }

    //#region IDisposable
    public override dispose(): void {
        if (this._isActionDisposed) {
            return;
        }
        this._isActionDisposed = true;
        this.toolbarButton?.dispose();

        this.enabledChanged.removeAll();
        this.visibilityChanged.removeAll();
        this.interactiveChanged.removeAll();
        this.enabledVisualChanged.removeAll();
        this.isActiveChanged.removeAll();
        this.ownerChanged.removeAll();

        super.dispose();
    }
}
